/**
 * MobileResearchDetail — the FULL single research item, rendered as prose.
 *
 * CLUSTER-2 (research-visibility uplift). The list (MobileResearch) shows only title/axis/conviction;
 * this screen renders the WHOLE item the caller is permitted to see — the body, and crucially the firm's
 * differentiated reasoning: the "House view" (why CBAM holds this view / where it diverges from
 * consensus) and the "Risks". Those two are the product's edge, so they're presented prominently as
 * prose, NOT buried in badges.
 *
 * Route: /partner/m/research/:id
 *
 * DATA — `mobile_item_detail(_item_id)` SECURITY DEFINER RPC, which re-checks the research_items RLS
 * SELECT predicate in-body. It returns either
 *   { ok: true,  item: { id, title, status, primary_axis, conviction, time_horizon, portfolio_level,
 *                        summary, body_markdown, ai_house_view_draft, ai_risks, updated_at } }
 *   { ok: false, reason: 'not_found_or_forbidden' }   ← deliberately opaque: missing vs forbidden are
 *                                                        NOT distinguished, so we show one calm message.
 *
 * READ-ONLY and offline-aware, mirroring the sibling mobile pages exactly:
 *   • ONE useCachedQuery (cacheKey scoped per id) write-throughs the resolved detail (queryFn THROWS on
 *     { error } so the cached last-known-good keeps serving offline).
 *   • The opaque { ok:false } is a valid, non-error state → a calm "not available" card, never a banner.
 *   • The "Saved <relative time>" header hint + amber-when-offline colour mirror the sibling pages.
 *
 * The generated supabase types are stale for this RPC, so the call is cast through `any`. No writes.
 * Web/desktop are untouched (this is a /partner/m/* surface).
 */
import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronLeft, AlertTriangle, Compass } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { humanise } from '@/components/research-hub/labels';

// ---------------------------------------------------------------------------
// Shapes — exactly the keys mobile_item_detail() builds.
// ---------------------------------------------------------------------------

interface ResearchItem {
  id: string;
  title: string | null;
  status: string | null;
  primary_axis: string | null;
  conviction: number | null;
  time_horizon: string | null;
  portfolio_level: number | null;
  summary: string | null;
  body_markdown: string | null;
  ai_house_view_draft: string | null;
  ai_risks: string | null;
  updated_at: string | null;
}

interface ItemDetailData {
  ok: boolean;
  item: ResearchItem | null;
}

const EMPTY_DETAIL: ItemDetailData = { ok: false, item: null };

async function fetchItemDetail(id: string): Promise<ItemDetailData> {
  const { data, error } = await (supabase.rpc as any)('mobile_item_detail', { _item_id: id });
  if (error) throw error;
  const raw = (data ?? {}) as { ok?: boolean; item?: ResearchItem | null };
  return { ok: raw.ok === true, item: raw.item ?? null };
}

// conviction is a 1..5 integer (desktop renders "Conviction n/5").
function convictionLabel(c: number | null): string | null {
  if (typeof c !== 'number' || !Number.isFinite(c)) return null;
  return `Conviction ${c}/5`;
}

// Shared prose styling for every markdown block — light + dark correct (dark:prose-invert).
const PROSE =
  'prose prose-sm max-w-none font-body prose-headings:font-heading prose-a:text-primary dark:prose-invert';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobileResearchDetail() {
  const online = useIsOnline();
  const { id = '' } = useParams<{ id: string }>();

  const detail = useCachedQuery<ItemDetailData>({
    queryKey: ['mobile-item-detail', id],
    cacheKey: `mobile.item_detail.${id}`,
    queryFn: () => fetchItemDetail(id),
    enabled: id.length > 0,
  });

  const data = detail.data ?? EMPTY_DETAIL;
  const item = data.item;

  const showStale = detail.isFromCache && detail.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && detail.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(detail.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  const hardError = detail.isError && !detail.isFromCache && detail.data === undefined;

  // The RPC resolved but the item isn't visible (or doesn't exist) — opaque by design.
  const notAvailable = !hardError && data.ok === false;

  const updatedLabel = useMemo(() => {
    if (!item?.updated_at) return null;
    try {
      return formatDistanceToNow(new Date(item.updated_at), { addSuffix: true });
    } catch {
      return null;
    }
  }, [item?.updated_at]);

  const conviction = convictionLabel(item?.conviction ?? null);
  const axis = item?.primary_axis ? humanise(item.primary_axis) : null;
  const horizon = item?.time_horizon ? humanise(item.time_horizon) : null;
  const portfolio =
    typeof item?.portfolio_level === 'number' && Number.isFinite(item.portfolio_level)
      ? `P${item.portfolio_level}`
      : null;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/research"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Research
        </Link>
        <p className="text-sm text-muted-foreground">Research item</p>
        <h1 className="font-heading text-2xl font-semibold text-primary">
          {item?.title ?? (notAvailable ? 'Not available' : 'Research')}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      <div className="space-y-3">
        {hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Couldn’t load</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load this item just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved copy of this item yet. This will fill in once you reconnect.'}
            </p>
          </section>
        ) : notAvailable ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <AlertTriangle className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-sm font-semibold">This item isn’t available</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              It may have been moved, or it isn’t shared with your access level.
            </p>
            <Link
              to="/partner/m/research"
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary active:opacity-80"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              Back to research
            </Link>
          </section>
        ) : item ? (
          <>
            {/* Quick-read badge row — axis / conviction / horizon / portfolio level. */}
            {(axis || conviction || horizon || portfolio) && (
              <div className="flex flex-wrap items-center gap-1.5">
                {axis && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {axis}
                  </span>
                )}
                {conviction && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                    {conviction}
                  </span>
                )}
                {horizon && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {horizon}
                  </span>
                )}
                {portfolio && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {portfolio}
                  </span>
                )}
              </div>
            )}

            {/* Summary lead, if present (distinct from the body). */}
            {item.summary && (
              <section className="rounded-xl border border-border bg-card p-4">
                <p className="text-sm text-foreground">{item.summary}</p>
              </section>
            )}

            {/* House view — the firm's differentiated reasoning, presented prominently as prose. */}
            {item.ai_house_view_draft && (
              <section className="rounded-xl border border-primary/30 bg-primary/5 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Compass className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <h2 className="font-heading text-sm font-semibold text-primary">House view</h2>
                </div>
                <div className={PROSE}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {item.ai_house_view_draft}
                  </ReactMarkdown>
                </div>
              </section>
            )}

            {/* Risks — presented prominently too, in the attention palette. */}
            {item.ai_risks && (
              <section className="rounded-xl border border-amber-300/60 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-900/15">
                <div className="mb-2 flex items-center gap-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600">
                    <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <h2 className="font-heading text-sm font-semibold text-amber-700 dark:text-amber-300">
                    Risks
                  </h2>
                </div>
                <div className={PROSE}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.ai_risks}</ReactMarkdown>
                </div>
              </section>
            )}

            {/* Full body. */}
            {item.body_markdown ? (
              <section className="rounded-xl border border-border bg-card p-4">
                <div className={PROSE}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.body_markdown}</ReactMarkdown>
                </div>
                {updatedLabel && (
                  <p className="mt-3 border-t border-border/60 pt-2 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                    Updated {updatedLabel}
                  </p>
                )}
              </section>
            ) : (
              !item.summary &&
              !item.ai_house_view_draft &&
              !item.ai_risks && (
                <section className="rounded-xl border border-border bg-card p-4">
                  <h2 className="font-heading text-sm font-semibold">No content yet</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    This item doesn’t have a written body yet.
                  </p>
                </section>
              )
            )}
          </>
        ) : (
          // enabled is false (empty id) or the very first paint before any data — quiet shell.
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-sm text-muted-foreground">Loading…</p>
          </section>
        )}
      </div>
    </div>
  );
}
